/**
 * notificationSound — Play a short "ping" chime and vibrate on mobile.
 * Uses the Web Audio API (no asset needed) plus navigator.vibrate.
 * Safe to call from anywhere; silently no-ops if audio is blocked.
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    return ctx;
  } catch {
    return null;
  }
}

/**
 * playPing — two-tone ascending chime.
 * @param volume 0..1 (default 0.25 — audible but not startling)
 */
export function playPing(volume = 0.25): void {
  try {
    const ac = getCtx();
    if (!ac) return;
    if (ac.state === "suspended") ac.resume().catch(() => {});

    const now = ac.currentTime;
    const tones: Array<[number, number]> = [
      [880, now],       // A5
      [1174.66, now + 0.12], // D6
    ];

    tones.forEach(([freq, start]) => {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(volume, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      osc.connect(gain).connect(ac.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    });
  } catch {
    /* ignore */
  }
}

/** Short vibration pattern on mobile. */
export function vibrate(pattern: number | number[] = [120, 60, 120]): void {
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  } catch {
    /* ignore */
  }
}

/** Combined alert: ping + vibrate. */
export function alertPing(): void {
  playPing();
  vibrate();
}
